import axios, {
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";

interface RefreshResponse {
  access: string;
  refresh?: string;  // New refresh token when rotation is enabled
}

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Set API URL based on environment
// In development, use the proxy defined in vue.config.js
// In production, use the Django backend directly
const isDevelopment = process.env.NODE_ENV === 'development';
const apiUrl = isDevelopment ? '/api' : (process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000');
console.log("API URL:", apiUrl, "Environment:", process.env.NODE_ENV);

const api = axios.create({
  baseURL: apiUrl,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
  // Enable credentials if your backend requires it
  withCredentials: false,
});

// Add request interceptor
api.interceptors.request.use(
  (config: CustomAxiosRequestConfig) => {
    // Log the full URL and request details
    const requestInfo = {
      url: `${api.defaults.baseURL}${config.url}`,
      method: config.method?.toUpperCase(),
      headers: config.headers,
      data: config.data
        ? {
            ...config.data,
            password: config.data.password ? "[REDACTED]" : undefined,
          }
        : undefined,
    };
    console.log("Making request:", requestInfo);

    // Get token from localStorage
    const token = localStorage.getItem("access_token");
    console.log("Token from localStorage:", token); // Debug log

    if (token) {
      // Set the Authorization header
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
      console.log("Request headers:", config.headers); // Debug log
    } else {
      console.warn("No token found in localStorage"); // Debug log
    }

    return config;
  },
  (error: unknown) => {
    console.error("Request error:", error);
    return Promise.reject(error);
  }
);

// Add response interceptor for token refresh
api.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log("Response:", {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
    });
    return response;
  },
  async (error: unknown) => {
    const axiosError = error as AxiosError;
    console.error("Response error details:", {
      status: axiosError.response?.status,
      statusText: axiosError.response?.statusText,
      data: axiosError.response?.data,
      message: axiosError.message,
      config: {
        url: axiosError.config?.url,
        method: axiosError.config?.method,
        headers: axiosError.config?.headers,
      },
    });

    if (axios.isAxiosError(axiosError)) {
      const originalRequest = axiosError.config as CustomAxiosRequestConfig;
      if (
        axiosError.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        originalRequest._retry = true;
        try {
          const refreshToken = localStorage.getItem("refresh_token");
          const response = await axios.post<RefreshResponse>(
            `${api.defaults.baseURL}/auth/refresh/`,
            {
              refresh: refreshToken,
            }
          );
          localStorage.setItem("access_token", response.data.access);
          // Update refresh token if rotation is enabled
          if (response.data.refresh) {
            localStorage.setItem("refresh_token", response.data.refresh);
          }
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
          return api(originalRequest);
        } catch (error) {
          // Handle refresh token error (logout user)
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          window.location.href = "/login";
          return Promise.reject(error);
        }
      }
    }
    return Promise.reject(error);
  }
);

export default api;
